import React, { useState, useEffect, useRef } from 'react';
import { updatePassword } from 'aws-amplify/auth';
import { Authenticator, ThemeProvider } from '@aws-amplify/ui-react';
import { Upload, User, Settings, Trash2, Download, Plus, Eye, X, Check, CreditCard, Calendar, Zap, Clock, CheckCircle, AlertCircle, Loader, RefreshCw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { userAPI, presentationsAPI, subscriptionAPI, notificationAPI } from '../services/api';
import { UploadProgress } from '../types';
import deckforgeLogo from '../deckforge-logo.png';

// Define TypeScript interfaces
interface Deck {
  userId: string;
  uploadId: string;
  name?: string;
  uploadDate: string;
  status: 'pending' | 'processing' | 'completed' | 'error';
  s3UploadedDocuments: string[];
  s3CompletedPresentation: string;
  textContentUrl?: string;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  errorMessage?: string;
  uploadMetadata?: {
    filesCount: number;
    hasTextContent: boolean;
    originalFileNames: string[];
  };
}

interface SubscriptionPlan {
  name: string;
  presentations: number | 'Unlimited';
  monthlyPrice: number;
  annualPrice: number;
}

interface UserProfile {
  username: string;
  email: string;
  name: string;
  subscription: keyof typeof subscriptionPlans;
  presentationsUsed: number;
  joinDate: string;
}

interface SubscriptionUsage {
  planId: string;
  planName: string;
  presentationsLimit: number | 'Unlimited';
  presentationsUsed: number;
  presentationsRemaining: number | 'Unlimited';
  percentageUsed: number;
  billingCycle: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  lastPeriodUsed: number;
}

interface LoginFormState {
  username: string;
  password: string;
  isSignUp: boolean;
}

// Define subscription plans
const subscriptionPlans: Record<string, SubscriptionPlan> = {
  free: { name: 'Free', presentations: 1, monthlyPrice: 0, annualPrice: 0 },
  basic: { name: 'Basic', presentations: 10, monthlyPrice: 15, annualPrice: 10 },
  pro: { name: 'Pro', presentations: 30, monthlyPrice: 30, annualPrice: 20 },
  enterprise: { name: 'Enterprise', presentations: 'Unlimited', monthlyPrice: 60, annualPrice: 45 }
};

// Loading animation component
const LoadingBar: React.FC = () => (
  <div className="bg-purple-100 rounded-full h-6 overflow-hidden">
    <div className="h-full bg-gradient-to-r from-purple-400 to-purple-600 rounded-full animate-pulse"></div>
  </div>
);

interface DeckForgeAppProps {
  formFields?: any;
  components?: any;
  theme?: any;
}

const DeckForgeApp: React.FC<DeckForgeAppProps> = ({ formFields, components, theme }) => {
  const [currentPage, setCurrentPage] = useState<string>('landing');
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loginForm, setLoginForm] = useState<LoginFormState>({ username: '', password: '', isSignUp: false });
  const [userDecks, setUserDecks] = useState<Deck[]>([]);
  const [isLoadingUserData, setIsLoadingUserData] = useState<boolean>(false);
  const [subscriptionUsage, setSubscriptionUsage] = useState<SubscriptionUsage | null>(null);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState<boolean>(false);
  const [changePasswordForm, setChangePasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [isChangingPassword, setIsChangingPassword] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [sseConnection, setSseConnection] = useState<EventSource | null>(null);
  const sseConnectionRef = useRef<EventSource | null>(null);
  const [notifications, setNotifications] = useState<string[]>([]);
  
  const { isAuthenticated, user: authUser, login, logout: authLogout } = useAuth();

  useEffect(() => {
    // Check for Stripe success/cancel URLs on page load
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId) {
      // User returned from Stripe Checkout with success
      setCurrentPage('subscription-success');
      // Update URL to show thank you page path
      window.history.replaceState({}, document.title, '/thank-you');
      return;
    }

    // Handle direct navigation to thank you page (only if not already navigating away)
    if (window.location.pathname === '/thank-you' && currentPage !== 'create' && currentPage !== 'dashboard') {
      setCurrentPage('subscription-success');
      return;
    }

    // Sync with auth context and fetch real data from backend
    console.log('Auth user changed:', authUser);
    if (authUser && isAuthenticated) {
      console.log('Setting user to:', authUser);
      setUser(authUser);
      
      // Fetch user's presentations from backend
      const fetchUserData = async () => {
        setIsLoadingUserData(true);
        try {
          // Fetch user data from backend with individual error handling
          const promises = [
            userAPI.getProfile().catch(err => {
              console.error('Error fetching user profile:', err);
              return { data: null, error: err };
            }),
            presentationsAPI.getAll().catch(err => {
              console.error('Error fetching presentations:', err);
              return { data: null, error: err };
            }),
            subscriptionAPI.getUsage().catch(err => {
              console.error('Error fetching subscription usage:', err);
              return { data: null, error: err };
            })
          ];
          
          const [userResponse, presentationsResponse, usageResponse] = await Promise.all(promises);
          
          // Update user data with backend response, but preserve the name if frontend has a better one
          if (userResponse.data) {
            const backendUser = userResponse.data;
            const isBackendNameUUID = backendUser.name && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(backendUser.name);
            const frontendHasGoodName = authUser?.name && authUser.name !== 'User' && !(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(authUser.name));
            
            // If backend returns UUID name but frontend has a good name, keep frontend name
            if (isBackendNameUUID && frontendHasGoodName) {
              console.log('Preserving frontend name:', authUser.name, 'instead of backend UUID:', backendUser.name);
              setUser({ ...backendUser, name: authUser.name });
            } else {
              console.log('Using backend user data:', backendUser);
              setUser(backendUser);
            }
          } else {
            console.warn('Failed to fetch user profile data');
          }
          
          // Update presentations with backend response
          if (presentationsResponse.data) {
            console.log('Presentations data received:', presentationsResponse.data.length, 'presentations');
            setUserDecks(presentationsResponse.data);
          } else {
            console.warn('Failed to fetch presentations data');
          }

          // Update subscription usage with backend response
          if (usageResponse.data) {
            console.log('Subscription usage data received:', usageResponse.data);
            setSubscriptionUsage(usageResponse.data);
          } else {
            console.warn('Failed to fetch subscription usage data. Response:', usageResponse);
            // Set a fallback subscription usage state to prevent UI issues
            setSubscriptionUsage(null);
          }
        } catch (error) {
          console.error('Unexpected error in fetchUserData:', error);
          // Keep the user logged in even if backend fails
        } finally {
          setIsLoadingUserData(false);
        }
      };
      
      fetchUserData();
      
      if (currentPage === 'landing' || currentPage === 'login') {
        setCurrentPage('dashboard');
      }
    } else {
      setUser(null);
      setUserDecks([]);
      setSubscriptionUsage(null);
      setIsLoadingUserData(false);
    }
  }, [authUser, isAuthenticated, currentPage]);

  // SSE connection management - Fixed version
  useEffect(() => {
    let eventSource: EventSource | null = null;
    let isConnecting = false;

    const connectSSE = async () => {
      if (isConnecting || eventSource) {
        console.log('SSE connection already in progress or exists');
        return;
      }

      if (!authUser || !isAuthenticated || currentPage !== 'dashboard') {
        return;
      }

      console.log('Establishing SSE connection for real-time presentation updates');
      isConnecting = true;

      try {
        eventSource = await notificationAPI.connectSSE(
          (data) => {
            if (data.type === 'presentation_update') {
              // Update the presentation in the local state
              const { uploadId, status } = data.data;
              setUserDecks(prevDecks => 
                prevDecks.map(deck => 
                  deck.uploadId === uploadId 
                    ? { ...deck, ...data.data }
                    : deck
                )
              );
              
              // Show a user-friendly notification
              const presentationName = data.data.name || `${uploadId.substring(0, 8)}...`;
              if (status === 'completed') {
                setNotifications(prev => [...prev, `Presentation "${presentationName}" is ready!`]);
              } else if (status === 'error') {
                setNotifications(prev => [...prev, `Presentation "${presentationName}" failed to process`]);
              } else if (status === 'processing') {
                setNotifications(prev => [...prev, `Presentation "${presentationName}" is being processed`]);
              }
            }
          },
          (error) => {
            console.warn('SSE connection error - not reconnecting automatically:', error);
          }
        );
        
        sseConnectionRef.current = eventSource;
        setSseConnection(eventSource);
        console.log('SSE connection established successfully');
        
      } catch (error) {
        console.error('Failed to establish SSE connection:', error);
      } finally {
        isConnecting = false;
      }
    };

    // Only connect if on dashboard and authenticated
    if (authUser && isAuthenticated && currentPage === 'dashboard') {
      connectSSE();
    }

    // Cleanup function
    return () => {
      if (eventSource) {
        console.log('Cleaning up SSE connection');
        eventSource.close();
        eventSource = null;
      }
      if (sseConnectionRef.current) {
        sseConnectionRef.current.close();
        sseConnectionRef.current = null;
      }
      setSseConnection(null);
    };
  }, [authUser, isAuthenticated, currentPage]);

  // Auto-clear notifications after 5 seconds
  useEffect(() => {
    if (notifications.length > 0) {
      const timer = setTimeout(() => {
        setNotifications(prev => prev.slice(1));
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notifications]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      await login(loginForm.username, loginForm.password);
      setLoginForm({ username: '', password: '', isSignUp: false });
      
      if (loginForm.isSignUp) {
        setCurrentPage('subscription');
      } else {
        setCurrentPage('dashboard');
      }
    } catch (error) {
      console.error('Login error:', error);
      alert('Login failed. Please check your credentials.');
    }
  };

  const handleLogout = async () => {
    try {
      await authLogout();
      setCurrentPage('landing');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (changePasswordForm.newPassword !== changePasswordForm.confirmPassword) {
      alert('New passwords do not match.');
      return;
    }
    
    if (changePasswordForm.newPassword.length < 8) {
      alert('New password must be at least 8 characters long.');
      return;
    }
    
    setIsChangingPassword(true);
    
    try {
      await updatePassword({
        oldPassword: changePasswordForm.currentPassword,
        newPassword: changePasswordForm.newPassword,
      });
      
      alert('Password changed successfully!');
      setShowChangePasswordModal(false);
      setChangePasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error: any) {
      console.error('Password change error:', error);
      
      // Handle specific Cognito errors
      if (error.name === 'NotAuthorizedException') {
        alert('Current password is incorrect.');
      } else if (error.name === 'InvalidPasswordException') {
        alert('New password does not meet requirements. Please ensure it has at least 8 characters.');
      } else if (error.name === 'LimitExceededException') {
        alert('Too many password change attempts. Please try again later.');
      } else {
        alert('Failed to change password. Please try again.');
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  // This function is now primarily used for free plan activation
  // Paid plans go through Stripe Pricing Table directly
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSubscriptionSelect = async (plan: string, billing: string) => {
    if (user && plan === 'free') {
      setIsLoadingUserData(true);
      try {
        await subscriptionAPI.subscribe(plan, billing);
        
        // Refresh user data
        try {
          const userResponse = await userAPI.getProfile();
          if (userResponse.data) {
            const backendUser = userResponse.data;
            const isBackendNameUUID = backendUser.name && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(backendUser.name);
            const currentHasGoodName = user?.name && user.name !== 'User' && !(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(user.name));
            
            if (isBackendNameUUID && currentHasGoodName) {
              setUser({ ...backendUser, name: user.name });
            } else {
              setUser(backendUser);
            }
          }
        } catch (profileError) {
          console.error('Error refreshing user profile after subscription:', profileError);
          const updatedUser = { ...user, subscription: plan as keyof typeof subscriptionPlans };
          setUser(updatedUser);
        }
        
        setCurrentPage('dashboard');
      } catch (error) {
        console.error('Error subscribing to plan:', error);
        alert('Failed to subscribe to the plan. Please try again.');
      } finally {
        setIsLoadingUserData(false);
      }
    }
  };


  const getRemainingPresentations = (): string | number => {
    if (!subscriptionUsage) return 0;
    
    if (subscriptionUsage.presentationsLimit === 'Unlimited') return '∞';
    return subscriptionUsage.presentationsRemaining;
  };

  const canCreatePresentation = (): boolean => {
    if (!subscriptionUsage) return false;
    if (subscriptionUsage.presentationsLimit === 'Unlimited') return true;
    return subscriptionUsage.presentationsRemaining > 0;
  };

  const getStatusIndicator = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <div className="flex items-center gap-2 text-yellow-600">
            <Clock className="w-4 h-4" />
            <span className="text-sm font-medium">Pending</span>
          </div>
        );
      case 'processing':
        return (
          <div className="flex items-center gap-2 text-blue-600">
            <Loader className="w-4 h-4 animate-spin" />
            <span className="text-sm font-medium">Processing</span>
          </div>
        );
      case 'completed':
        return (
          <div className="flex items-center gap-2 text-green-600">
            <CheckCircle className="w-4 h-4" />
            <span className="text-sm font-medium">Completed</span>
          </div>
        );
      case 'error':
        return (
          <div className="flex items-center gap-2 text-red-600">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm font-medium">Error</span>
          </div>
        );
      default:
        return (
          <div className="flex items-center gap-2 text-gray-600">
            <Clock className="w-4 h-4" />
            <span className="text-sm font-medium">Unknown</span>
          </div>
        );
    }
  };

  const handleDownload = async (presentation: Deck) => {
    if (presentation.status !== 'completed') {
      alert('Presentation is not ready for download yet.');
      return;
    }
    
    try {
      // Get pre-signed download URL from backend
      const response = await presentationsAPI.download(presentation.uploadId);
      const { downloadUrl } = response.data;
      
      // Open the pre-signed URL in a new tab to download
      window.open(downloadUrl, '_blank');
    } catch (error) {
      console.error('Error downloading presentation:', error);
      alert('Failed to download presentation. Please try again.');
    }
  };

  const refreshPresentations = async () => {
    if (isLoadingUserData) return; // Prevent multiple simultaneous requests
    
    setIsLoadingUserData(true);
    try {
      const [presentationsResponse, usageResponse] = await Promise.all([
        presentationsAPI.getAll(),
        subscriptionAPI.getUsage()
      ]);
      
      if (presentationsResponse.data) {
        setUserDecks(presentationsResponse.data);
      }
      
      if (usageResponse.data) {
        setSubscriptionUsage(usageResponse.data);
      }
    } catch (error) {
      console.error('Error refreshing presentations:', error);
      alert('Failed to refresh presentations. Please try again.');
    } finally {
      setIsLoadingUserData(false);
    }
  };

  const handleDeletePresentation = async (uploadId: string) => {
    if (!window.confirm('Are you sure you want to delete this presentation? This action cannot be undone.')) {
      return;
    }

    try {
      await presentationsAPI.delete(uploadId);
      
      // Remove the presentation from the local state immediately
      setUserDecks(userDecks.filter(deck => deck.uploadId !== uploadId));
      
      // Optionally refresh the data from server
      await refreshPresentations();
      
    } catch (error) {
      console.error('Error deleting presentation:', error);
      alert('Failed to delete presentation. Please try again.');
    }
  };

  // Landing Page Component
  const LandingPage: React.FC = () => (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-blue-300 to-blue-600">
      {/* Navigation */}
      <nav className="flex justify-between items-center px-6 py-1.5 text-blue-900">
        <div className="flex items-center gap-1">
          <img src={deckforgeLogo} alt="DeckForge Logo" className="w-16 h-16 rounded-lg" style={{width: '140px', height: '140px'}} />
          <span className="text-4xl font-bold text-blue-900">DeckForge</span>
        </div>
        <div className="flex gap-4">
          {user ? (
            <>
              <button onClick={() => setCurrentPage('dashboard')} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                Dashboard
              </button>
              <button onClick={handleLogout} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                Logout
              </button>
            </>
          ) : (
            <button onClick={() => setCurrentPage('login')} className="px-6 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors">
              Login
            </button>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <div className="max-w-6xl mx-auto px-6 py-12 text-center">
        <h1 className="text-5xl sm:text-6xl font-bold mb-4 leading-tight text-blue-900">
          AI-Powered Presentations<br />
          <span className="bg-gradient-to-r from-yellow-500 to-orange-500 bg-clip-text text-transparent">
            in Seconds
          </span>
        </h1>
        <p className="text-lg sm:text-xl mb-8 text-blue-900 max-w-3xl mx-auto">
          Transform your PDFs, documents, and ideas into stunning professional presentations. 
          No design skills required – just upload and watch the magic happen.
        </p>

        <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-10">
          <button 
            onClick={() => user ? setCurrentPage('dashboard') : setCurrentPage('login')}
            className="group px-8 py-4 bg-gradient-to-r from-orange-500 to-pink-500 text-white font-bold text-lg rounded-xl hover:from-orange-600 hover:to-pink-600 transition-all transform hover:scale-105 shadow-2xl flex items-center gap-3"
          >
            <Upload className="w-6 h-6" />
            Create Your First Presentation
            <span className="group-hover:translate-x-1 transition-transform">→</span>
          </button>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          <div className="bg-white/20 backdrop-blur-sm p-6 rounded-2xl border border-white/30 shadow-lg">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center mb-4 mx-auto">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-lg font-bold mb-3 text-blue-900">Lightning Fast</h3>
            <p className="text-sm text-blue-900">Transform any document into a professional presentation in under 60 seconds</p>
          </div>
          <div className="bg-white/20 backdrop-blur-sm p-6 rounded-2xl border border-white/30 shadow-lg">
            <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mb-4 mx-auto">
              <Eye className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-lg font-bold mb-3 text-blue-900">Stunning Design</h3>
            <p className="text-sm text-blue-900">AI-powered layouts that look professionally designed every time</p>
          </div>
          <div className="bg-white/20 backdrop-blur-sm p-6 rounded-2xl border border-white/30 shadow-lg">
            <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-orange-500 rounded-2xl flex items-center justify-center mb-4 mx-auto">
              <Settings className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-lg font-bold mb-3 text-blue-900">Brand Perfect</h3>
            <p className="text-sm text-blue-900">Automatically matches your brand colors, fonts, and style guidelines</p>
          </div>
        </div>
      </div>

      {/* Cognito handles login/register with its own UI */}
    </div>
  );

  // Subscription Plans Page using Stripe Pricing Table
  const SubscriptionPage: React.FC = () => {
    useEffect(() => {
      // Load Stripe pricing table script
      const script = document.createElement('script');
      script.src = 'https://js.stripe.com/v3/pricing-table.js';
      script.async = true;
      document.head.appendChild(script);

      return () => {
        // Cleanup script on unmount
        const existingScript = document.querySelector('script[src="https://js.stripe.com/v3/pricing-table.js"]');
        if (existingScript) {
          document.head.removeChild(existingScript);
        }
      };
    }, []);

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-blue-300 to-blue-600">
        {/* Header */}
        <div className="bg-white/90 backdrop-blur-sm shadow-sm border-b border-white/30">
          <div className="max-w-6xl mx-auto px-6 py-1 flex justify-between items-center">
            <div className="flex items-center gap-1">
              <img src={deckforgeLogo} alt="DeckForge Logo" className="w-16 h-16 rounded-lg" style={{width: '70px', height: '70px'}} />
              <span className="text-4xl font-bold text-blue-900">DeckForge</span>
            </div>
            <div className="flex items-center gap-4">
              <button 
                onClick={() => setCurrentPage('dashboard')}
                className="text-lg font-bold text-blue-700 hover:text-blue-900"
              >
                Dashboard
              </button>
              <button onClick={handleLogout} className="text-lg font-bold text-blue-700 hover:text-blue-900">
                Logout
              </button>
            </div>
          </div>
        </div>

        <div className="w-full mx-auto px-4 sm:px-6 py-12">
          <div className="text-center mb-12 max-w-4xl mx-auto">
            <h1 className="text-4xl font-bold text-blue-900 mb-4">Choose Your Plan</h1>
            <p className="text-xl text-blue-800 mb-8">Select the perfect plan for your presentation needs</p>
            
            {user?.subscription && (
              <div className="inline-flex flex-col items-center px-4 py-2 bg-blue-100 text-blue-800 rounded-lg mb-8">
                <div className="flex items-center">
                  <CreditCard className="w-4 h-4 mr-2" />
                  Current Plan: {subscriptionPlans[user.subscription]?.name || user.subscription}
                </div>
                {user.subscription === 'free' && (
                  <p className="text-sm text-blue-600 mt-1 text-center">
                    You have 1 presentation per month. Upgrade to create more!
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Stripe Pricing Table */}
          <div className="w-full overflow-x-auto">
            <div className="min-w-full" style={{ minWidth: '320px' }}>
              {React.createElement('stripe-pricing-table', {
                'pricing-table-id': 'prctbl_1RYBfQBkOq7hTD7jfRIo6MaZ',
                'publishable-key': 'pk_live_hr31YPFajDBHmuSmdh2pGszH00mgoWiHxa',
                'client-reference-id': user?.username || '',
                'customer-email': user?.email || ''
              })}
            </div>
          </div>

          {/* Additional Information */}
          <div className="mt-12 text-center">
            <p className="text-blue-800 mb-4">
              All plans include AI-Powered content and design, pptx export format, and secure cloud storage.
            </p>
            <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              <div className="flex items-center justify-center gap-2 text-blue-800">
                <Check className="w-5 h-5 text-green-500" />
                <span>Change plans anytime</span>
              </div>
              <div className="flex items-center justify-center gap-2 text-blue-800">
                <Check className="w-5 h-5 text-green-500" />
                <span>Email support available</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Dashboard Page
  const DashboardPage: React.FC = () => (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-blue-300 to-blue-600">
      {/* Header */}
      <div className="bg-white/90 backdrop-blur-sm shadow-sm border-b border-white/30">
        <div className="max-w-6xl mx-auto px-6 py-1 flex justify-between items-center">
          <div className="flex items-center gap-1">
            <img src={deckforgeLogo} alt="DeckForge Logo" className="w-16 h-16 rounded-lg" style={{width: '70px', height: '70px'}} />
            <span className="text-4xl font-bold text-blue-900">DeckForge</span>
          </div>
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setCurrentPage('profile')}
              className="flex items-center gap-2 px-4 py-2 text-lg font-bold text-blue-700 hover:text-blue-900 hover:bg-blue-100 rounded-lg transition-colors"
            >
              <User className="w-5 h-5" />
              Profile
            </button>
            <button onClick={handleLogout} className="text-lg font-bold text-blue-700 hover:text-blue-900">
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-blue-900 mb-2">Welcome back, {user?.name}!</h1>
          <p className="text-blue-800">Create stunning presentations in seconds with AI-powered design.</p>
        </div>

        {/* Real-time Notifications */}
        {notifications.length > 0 && (
          <div className="fixed top-4 right-4 z-50 space-y-2">
            {notifications.map((notification, index) => (
              <div 
                key={index}
                className="bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 animate-slide-in"
              >
                <CheckCircle className="w-5 h-5" />
                <span>{notification}</span>
                <button
                  onClick={() => setNotifications(prev => prev.filter((_, i) => i !== index))}
                  className="ml-2 text-white hover:text-green-200"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white/80 backdrop-blur-sm p-6 rounded-xl border border-white/40 shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="text-sm font-medium text-blue-700 mb-2">Presentations Remaining</h3>
                {isLoadingUserData || !subscriptionUsage ? (
                  <LoadingBar />
                ) : (
                  <p className="text-2xl font-bold text-blue-900">{getRemainingPresentations()}</p>
                )}
              </div>
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                <Upload className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
          <div className="bg-white/80 backdrop-blur-sm p-6 rounded-xl border border-white/40 shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="text-sm font-medium text-blue-700 mb-2">Used This Month</h3>
                {isLoadingUserData || !subscriptionUsage ? (
                  <LoadingBar />
                ) : (
                  <p className="text-2xl font-bold text-blue-900">
                    {subscriptionUsage.presentationsUsed || 0}
                    {subscriptionUsage.presentationsLimit && subscriptionUsage.presentationsLimit !== 'Unlimited' && 
                      <span className="text-sm text-blue-600">/{subscriptionUsage.presentationsLimit}</span>
                    }
                  </p>
                )}
              </div>
              <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                <Eye className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
          <div className="bg-white/80 backdrop-blur-sm p-6 rounded-xl border border-white/40 shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="text-sm font-medium text-blue-700 mb-2">Current Plan</h3>
                {isLoadingUserData || !user || !user.subscription ? (
                  <LoadingBar />
                ) : (
                  <p className="text-2xl font-bold text-blue-900">{subscriptionPlans[user.subscription]?.name}</p>
                )}
              </div>
              <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-blue-500 rounded-lg flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Create New Deck Button */}
        <div className="mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => canCreatePresentation() && setCurrentPage('create')}
              disabled={!canCreatePresentation()}
              className={`flex items-center gap-3 px-6 py-4 font-semibold rounded-xl transition-colors shadow-lg ${
                canCreatePresentation()
                  ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              <Plus className="w-5 h-5" />
              Create New Presentation
            </button>
            
            {!canCreatePresentation() && subscriptionUsage && (
              <button
                onClick={() => setCurrentPage('subscription')}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white font-medium rounded-lg hover:from-orange-600 hover:to-red-600 transition-colors shadow-md"
              >
                <Zap className="w-4 h-4" />
                Upgrade Now to Get More Presentations
              </button>
            )}
          </div>
          
          {!canCreatePresentation() && subscriptionUsage && (
            <p className="text-sm text-gray-600 mt-2">
              You've used {subscriptionUsage.presentationsUsed} of {subscriptionUsage.presentationsLimit} presentations this month. 
              Your limit resets on {new Date(subscriptionUsage.currentPeriodEnd).toLocaleDateString()}.
            </p>
          )}
        </div>

        {/* Presentations Table */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-blue-900">Your Presentations</h2>
            <button
              onClick={refreshPresentations}
              disabled={isLoadingUserData}
              className="flex items-center gap-2 px-3 py-2 text-sm text-blue-700 hover:text-blue-900 hover:bg-blue-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`w-4 h-4 ${isLoadingUserData ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
          {isLoadingUserData ? (
            <div className="bg-white/80 backdrop-blur-sm rounded-xl border border-white/40 shadow-lg p-8">
              <div className="flex items-center justify-center">
                <Loader className="w-6 h-6 animate-spin text-purple-600" />
                <span className="ml-2 text-blue-800">Loading presentations...</span>
              </div>
            </div>
          ) : userDecks.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Upload className="w-12 h-12 text-blue-600" />
              </div>
              <h3 className="text-lg font-medium text-blue-900 mb-2">No presentations yet</h3>
              <p className="text-blue-800 mb-6">Create your first AI-powered presentation to get started</p>
              <div className="flex flex-col items-center gap-3">
                <button
                  onClick={() => canCreatePresentation() && setCurrentPage('create')}
                  disabled={!canCreatePresentation()}
                  className={`px-6 py-3 font-semibold rounded-lg transition-colors ${
                    canCreatePresentation()
                      ? 'bg-purple-600 text-white hover:bg-purple-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Create Your First Presentation
                </button>
                
                {!canCreatePresentation() && subscriptionUsage && (
                  <>
                    <button
                      onClick={() => setCurrentPage('subscription')}
                      className="px-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white font-medium rounded-lg hover:from-orange-600 hover:to-red-600 transition-colors"
                    >
                      <Zap className="w-4 h-4 inline-block mr-1" />
                      Upgrade to Create Presentations
                    </button>
                    <p className="text-sm text-gray-600 text-center">
                      You've reached your monthly limit of {subscriptionUsage.presentationsLimit} presentations.
                    </p>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white/80 backdrop-blur-sm rounded-xl border border-white/40 shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-blue-50/80 border-b border-blue-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">
                        Presentation Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">
                        Created Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">
                        Files
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {userDecks
                      .sort((a, b) => new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime())
                      .map(presentation => (
                      <tr key={presentation.uploadId} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col">
                            <div className="text-sm font-medium text-gray-900">
                              {presentation.name || 'Untitled Presentation'}
                            </div>
                            <div className="text-xs text-gray-500">
                              ID: {presentation.uploadId.substring(0, 12)}...
                            </div>
                            {presentation.uploadMetadata && (
                              <div className="text-xs text-gray-500">
                                {presentation.uploadMetadata.filesCount} file(s)
                                {presentation.uploadMetadata.hasTextContent && ' + text content'}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <Calendar className="w-4 h-4" />
                            {new Date(presentation.uploadDate).toLocaleDateString()}
                          </div>
                          <div className="text-xs text-gray-500">
                            {new Date(presentation.uploadDate).toLocaleTimeString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusIndicator(presentation.status)}
                          {presentation.status === 'error' && presentation.errorMessage && (
                            <div className="text-xs text-red-500 mt-1">
                              {presentation.errorMessage}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col gap-1">
                            {presentation.uploadMetadata?.originalFileNames?.map((fileName, index) => (
                              <div key={index} className="text-xs text-gray-600 truncate max-w-[200px]">
                                {fileName}
                              </div>
                            )) || (
                              <div className="text-xs text-gray-500">No files</div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleDownload(presentation)}
                              disabled={presentation.status !== 'completed'}
                              className={`flex items-center gap-1 px-3 py-1 text-sm rounded-lg transition-colors ${
                                presentation.status === 'completed'
                                  ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                  : 'bg-gray-100 text-gray-500 cursor-not-allowed'
                              }`}
                            >
                              <Download className="w-4 h-4" />
                              Download
                            </button>
                            <button
                              onClick={() => handleDeletePresentation(presentation.uploadId)}
                              className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Profile Page
  const ProfilePage: React.FC = () => (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-blue-300 to-blue-600">
      {/* Header */}
      <div className="bg-white/90 backdrop-blur-sm shadow-sm border-b border-white/30">
        <div className="max-w-6xl mx-auto px-6 py-1 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <button onClick={() => setCurrentPage('dashboard')} className="text-lg font-bold text-blue-700 hover:text-blue-900">
              ← Back to Dashboard
            </button>
          </div>
          <button onClick={handleLogout} className="text-lg font-bold text-blue-700 hover:text-blue-900">
            Logout
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <h1 className="text-3xl font-bold text-blue-900 mb-8">Account Settings</h1>

        <div className="space-y-8">
          {/* User Details */}
          <div className="bg-white/80 backdrop-blur-sm p-6 rounded-xl border border-white/40 shadow-lg">
            <h2 className="text-xl font-semibold text-blue-900 mb-6">Profile Information</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-blue-700 mb-2">Full Name</label>
                <input
                  type="text"
                  value={user?.name || ''}
                  className="w-full px-4 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  readOnly
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-blue-700 mb-2">Email</label>
                <input
                  type="email"
                  value={user?.email || ''}
                  className="w-full px-4 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  readOnly
                />
              </div>
            </div>
            <div className="mt-6">
              <button 
                onClick={() => setShowChangePasswordModal(true)}
                className="px-4 py-2 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors"
              >
                Change Password
              </button>
            </div>
          </div>

          {/* Subscription Details */}
          <div className="bg-white/80 backdrop-blur-sm p-6 rounded-xl border border-white/40 shadow-lg">
            <h2 className="text-xl font-semibold text-blue-900 mb-6">Subscription</h2>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-medium text-blue-900">{user && subscriptionPlans[user.subscription]?.name} Plan</h3>
                <p className="text-blue-700">
                  {user && subscriptionPlans[user.subscription]?.presentations === 'Unlimited' 
                    ? 'Unlimited presentations per month'
                    : `${user && subscriptionPlans[user.subscription]?.presentations} presentations per month`
                  }
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage('subscription')}
                  className="px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Change Plan
                </button>
                {user && user.subscription !== 'free' && (
                  <button
                    onClick={() => window.open('https://billing.stripe.com/p/login/00w7sK622fbu0zC5bg5Vu00', '_blank')}
                    className="px-4 py-2 bg-blue-100 text-blue-700 font-medium rounded-lg hover:bg-blue-200 transition-colors"
                  >
                    Manage Billing
                  </button>
                )}
              </div>
            </div>
            
            <div className="bg-blue-50/60 p-4 rounded-lg mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-blue-700">Presentations Used This Month</span>
                <span className="text-sm font-bold text-blue-900">
                  {subscriptionUsage?.presentationsUsed || 0} / {subscriptionUsage?.presentationsLimit || 'Loading...'}
                </span>
              </div>
              {subscriptionUsage && subscriptionUsage.presentationsLimit !== 'Unlimited' && (
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-purple-500 to-blue-500 h-2 rounded-full transition-all"
                    style={{ width: `${Math.min(100, subscriptionUsage.percentageUsed || 0)}%` }}
                  ></div>
                </div>
              )}
              {subscriptionUsage && (
                <div className="mt-3 text-xs text-blue-600">
                  <div className="flex justify-between">
                    <span>Current period: {new Date(subscriptionUsage.currentPeriodStart).toLocaleDateString()} - {new Date(subscriptionUsage.currentPeriodEnd).toLocaleDateString()}</span>
                    <span>{subscriptionUsage.presentationsRemaining === 'Unlimited' ? 'Unlimited remaining' : `${subscriptionUsage.presentationsRemaining} remaining`}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );

  // Login Page with Cognito Authenticator
  const LoginPage: React.FC = () => (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-purple-600 to-purple-800">
      <ThemeProvider theme={theme}>
        <Authenticator 
          formFields={formFields}
          components={components}
          socialProviders={[]}
          loginMechanisms={['email']}
          signUpAttributes={['given_name', 'family_name', 'email']}
          hideSignUp={false}
        >
          {({ signOut }) => {
            // User is now authenticated - redirect to dashboard immediately
            console.log('Cognito authentication successful');
            console.log('Setting page to dashboard after authentication');
            setCurrentPage('dashboard');
            
            return <div>Redirecting...</div>;
          }}
        </Authenticator>
      </ThemeProvider>
    </div>
  );

  // Create Deck Page
  const CreateDeckPage: React.FC = () => {
    const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
    const [textInput, setTextInput] = useState('');
    const [presentationName, setPresentationName] = useState('');
    const [uploadProgress, setUploadProgress] = useState<UploadProgress>({
      percentage: 0,
      status: 'idle'
    });
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleDrag = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.type === 'dragenter' || e.type === 'dragover') {
        setDragActive(true);
      } else if (e.type === 'dragleave') {
        setDragActive(false);
      }
    };

    const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      
      const files = Array.from(e.dataTransfer.files);
      const validFiles = files.filter(file => {
        const extension = file.name.toLowerCase().split('.').pop();
        return ['pdf', 'ppt', 'pptx'].includes(extension || '');
      });
      
      if (validFiles.length > 0) {
        setUploadedFiles(prev => [...prev, ...validFiles]);
      }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      const validFiles = files.filter(file => {
        const extension = file.name.toLowerCase().split('.').pop();
        return ['pdf', 'ppt', 'pptx'].includes(extension || '');
      });
      
      if (validFiles.length > 0) {
        setUploadedFiles(prev => [...prev, ...validFiles]);
      }
    };

    const removeFile = (index: number) => {
      setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async () => {
      if (presentationName.trim() === '') {
        alert('Please enter a name for your presentation.');
        return;
      }
      
      if (uploadedFiles.length === 0 && textInput.trim() === '') {
        alert('Please upload a document or enter text to create your presentation.');
        return;
      }

      setUploadProgress({ percentage: 0, status: 'uploading' });

      try {
        const formData = new FormData();
        
        // Add files to form data
        uploadedFiles.forEach((file) => {
          formData.append('files', file);
        });
        
        // Add text input
        if (textInput.trim()) {
          formData.append('textContent', textInput.trim());
        }
        
        // Add presentation name
        formData.append('presentationName', presentationName.trim());

        // Upload using the presentations API
        await presentationsAPI.create(formData);
        
        setUploadProgress({ percentage: 100, status: 'completed', message: 'Presentation created successfully!' });
        
        // Reset form and redirect after a delay
        setTimeout(() => {
          setUploadedFiles([]);
          setTextInput('');
          setPresentationName('');
          setUploadProgress({ percentage: 0, status: 'idle' });
          setCurrentPage('dashboard');
        }, 2000);

      } catch (error: any) {
        console.error('Upload error:', error);
        
        // Get more detailed error message
        let errorMessage = 'Failed to create presentation. Please try again.';
        if (error.response) {
          // Server responded with error status
          errorMessage = error.response.data?.message || `Server error: ${error.response.status}`;
          console.error('Server response:', error.response.data);
        } else if (error.request) {
          // Request was made but no response received
          errorMessage = 'No response from server. Please check your connection.';
          console.error('No response received:', error.request);
        } else {
          // Something else happened
          errorMessage = error.message || 'Unknown error occurred';
        }
        
        setUploadProgress({ 
          percentage: 0, 
          status: 'error', 
          message: errorMessage 
        });
      }
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-blue-300 to-blue-600">
        {/* Header */}
        <div className="bg-white/90 backdrop-blur-sm shadow-sm border-b border-white/30">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-1 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <button 
                onClick={() => setCurrentPage('dashboard')} 
                className="text-lg font-bold text-blue-700 hover:text-blue-900 flex items-center gap-2"
              >
                ← <span className="hidden sm:inline">Back to Dashboard</span><span className="sm:hidden">Back</span>
              </button>
            </div>
            <button onClick={handleLogout} className="text-lg font-bold text-blue-700 hover:text-blue-900">
              Logout
            </button>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
          <div className="bg-white/90 backdrop-blur-sm p-4 sm:p-6 rounded-xl border border-white/40 shadow-lg">
            {/* Hero Section */}
            <div className="text-center mb-4 sm:mb-6">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3">
                <Upload className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
              </div>
              <h1 className="text-xl sm:text-2xl font-bold text-blue-900 mb-2">Create New Presentation</h1>
              <p className="text-sm sm:text-base text-blue-800 max-w-2xl mx-auto">
                Upload documents for inspiration or content, and provide direction for your deck.
              </p>
            </div>

            {/* Main Content - Vertical Stack */}
            <div className="space-y-4">
              
              {/* Presentation Name Section */}
              <div>
                <div className="mb-3">
                  <h2 className="text-base sm:text-lg font-semibold text-blue-900 mb-2">Presentation Name</h2>
                </div>
                
                <div className="relative">
                  <input
                    type="text"
                    value={presentationName}
                    onChange={(e) => setPresentationName(e.target.value)}
                    maxLength={100}
                    placeholder="Enter presentation name (e.g., Q4 Sales Report, Marketing Strategy 2024...)"
                    className="w-full p-3 sm:p-4 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                  />
                  <div className="absolute bottom-2 sm:bottom-3 right-2 sm:right-3 text-xs text-blue-600">
                    {presentationName.length}/100
                  </div>
                </div>
              </div>
              
              {/* Text Input Section */}
              <div>
                <div className="mb-3">
                  <h2 className="text-base sm:text-lg font-semibold text-blue-900 mb-2">Presentation Brief</h2>
                </div>
                
                <div className="relative">
                  <textarea
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    maxLength={1000}
                    placeholder="Enter your presentation topic, key points, target audience, or any specific requirements..."
                    className="w-full h-24 sm:h-28 p-3 border border-blue-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                  />
                  <div className="absolute bottom-2 sm:bottom-3 right-2 sm:right-3 text-xs text-blue-600">
                    {textInput.length}/1000
                  </div>
                </div>
              </div>

              {/* File Upload Section */}
              <div>
                <div className="mb-3">
                  <h2 className="text-base sm:text-lg font-semibold text-blue-900 mb-2">Upload Documents (Optional)</h2>
                </div>
                
                <div
                  className={`relative border-2 border-dashed rounded-lg p-3 text-center transition-colors ${
                    dragActive 
                      ? 'border-blue-400 bg-blue-50' 
                      : 'border-blue-300 hover:border-blue-400'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.ppt,.pptx"
                    onChange={handleFileSelect}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  <Upload className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                  <p className="text-sm font-medium text-blue-800 mb-1">
                    Drop files or click to browse
                  </p>
                  <p className="text-xs text-blue-600">
                    PDF, PPT, PPTX supported
                  </p>
                </div>

                {/* Uploaded Files List */}
                {uploadedFiles.length > 0 && (
                  <div className="mt-3 space-y-1">
                    <h3 className="text-xs sm:text-sm font-medium text-blue-800">Uploaded Files:</h3>
                    <div className="max-h-20 overflow-y-auto space-y-1">
                      {uploadedFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between bg-blue-50/60 p-2 sm:p-3 rounded-lg">
                          <span className="text-xs sm:text-sm text-blue-800 truncate pr-2">{file.name}</span>
                          <button
                            onClick={() => removeFile(index)}
                            className="text-red-500 hover:text-red-700 text-xs sm:text-sm font-medium flex-shrink-0"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Upload Progress */}
            {uploadProgress.status !== 'idle' && (
              <div className="mt-4 sm:mt-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    {uploadProgress.status === 'uploading' && 'Creating presentation...'}
                    {uploadProgress.status === 'completed' && 'Success!'}
                    {uploadProgress.status === 'error' && 'Error'}
                  </span>
                  {uploadProgress.status === 'uploading' && (
                    <span className="text-sm text-gray-500">{uploadProgress.percentage}%</span>
                  )}
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${
                      uploadProgress.status === 'completed' ? 'bg-green-500' :
                      uploadProgress.status === 'error' ? 'bg-red-500' : 'bg-purple-500'
                    }`}
                    style={{ width: `${uploadProgress.percentage}%` }}
                  />
                </div>
                {uploadProgress.message && (
                  <p className={`text-sm mt-2 ${
                    uploadProgress.status === 'completed' ? 'text-green-600' :
                    uploadProgress.status === 'error' ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {uploadProgress.message}
                  </p>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mt-4 sm:mt-6">
              <button
                onClick={() => setCurrentPage('dashboard')}
                className="w-full sm:w-auto px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors order-2 sm:order-1"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={uploadProgress.status === 'uploading'}
                className="w-full sm:w-auto px-8 py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors order-1 sm:order-2"
              >
                {uploadProgress.status === 'uploading' ? 'Creating...' : 'Create Presentation'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Subscription Success Page
  const SubscriptionSuccessPage: React.FC = () => {

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto bg-white p-8 rounded-2xl border border-gray-200 shadow-lg text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Subscription Successful!</h1>
          <p className="text-gray-600 mb-8">
            Welcome to your new plan! You can now start creating amazing presentations.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => {
                console.log('Start Creating clicked');
                // Clear URL parameters and navigate to create page
                window.history.replaceState({}, document.title, '/');
                setCurrentPage('create');
              }}
              className="w-full px-6 py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors"
            >
              Start Creating
            </button>
            <button
              onClick={() => {
                console.log('Go to Dashboard clicked');
                // Clear URL parameters and navigate to dashboard
                window.history.replaceState({}, document.title, '/');
                setCurrentPage('dashboard');
              }}
              className="w-full px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Route rendering
  const renderPage = () => {
    switch(currentPage) {
      case 'login':
        return <LoginPage />;
      case 'subscription':
        return <SubscriptionPage />;
      case 'subscription-success':
        return <SubscriptionSuccessPage />;
      case 'dashboard':
        return <DashboardPage />;
      case 'profile':
        return <ProfilePage />;
      case 'create':
        return <CreateDeckPage />;
      default:
        return <LandingPage />;
    }
  };

  return (
    <>
      {renderPage()}
      
      {/* Change Password Modal */}
      {showChangePasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900">Change Password</h2>
              <button
                onClick={() => setShowChangePasswordModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Password
                </label>
                <input
                  type="password"
                  value={changePasswordForm.currentPassword}
                  onChange={(e) => setChangePasswordForm(prev => ({
                    ...prev,
                    currentPassword: e.target.value
                  }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Password
                </label>
                <input
                  type="password"
                  value={changePasswordForm.newPassword}
                  onChange={(e) => setChangePasswordForm(prev => ({
                    ...prev,
                    newPassword: e.target.value
                  }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  minLength={8}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={changePasswordForm.confirmPassword}
                  onChange={(e) => setChangePasswordForm(prev => ({
                    ...prev,
                    confirmPassword: e.target.value
                  }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  minLength={8}
                  required
                />
              </div>
              
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowChangePasswordModal(false)}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isChangingPassword}
                  className="flex-1 px-4 py-3 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isChangingPassword ? (
                    <Loader className="w-5 h-5 animate-spin" />
                  ) : (
                    'Change Password'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default DeckForgeApp;